import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {BackendApi} from '../../api/api.ts';

const INACTIVITY_TIMEOUT = 2 * 60 * 1000;
const POLL_INTERVAL = 1000;
const MAX_EMPTY_RETRIES = 120;

interface SyncStatus {
    hasJobStarted: boolean;
    progress: number;
    totalTransactions: number;
    error: string;
    isFinished: boolean;
}

export function useStartBankSync(ref: string | null, token: string): SyncStatus {
    const [jobGroupId, setJobGroupId] = useState<string>();
    const [hasJobStarted, setHasJobStarted] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [totalTransactions, setTotalTransactions] = useState<number>(0);
    const [error, setError] = useState<string>('');
    const [isFinished, setIsFinished] = useState<boolean>(false);

    const lastActivityRef = useRef<number>(Date.now());
    const hasRequestedStartRef = useRef<boolean>(false);
    const backendApi = useMemo(() => new BackendApi(token), [token]);

    const updateLastActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
    }, []);

    useEffect(() => {
        if (!ref || isFinished || jobGroupId || error || hasRequestedStartRef.current) return;

        hasRequestedStartRef.current = true;

        backendApi.StartImportJob({ bank_connection_reference_id: ref })
            .then(data => {
                setJobGroupId(data.jobs_group_id);
                updateLastActivity();
            })
            .catch(() => {
                hasRequestedStartRef.current = false;
                setError('Er is iets fout gegaan bij het starten van de synchronisatie met de bank. Probeer het over een ogenblik opnieuw. ERR-71238');
            });
    }, [ref, backendApi, isFinished, jobGroupId, error, updateLastActivity]);


    useEffect(() => {
        if (!jobGroupId || isFinished || error) return;

        let emptyRetryCount = 0;
        
        const poll = async () => {
            try {
                const jobProgressData = await backendApi.GetImportJobProgress(jobGroupId)

                if (!jobProgressData) {
                    if (++emptyRetryCount > MAX_EMPTY_RETRIES) {
                        setJobGroupId(undefined);
                        setError('We konden geen synchronisatie-opdrachten vinden. Probeer het over een ogenblik opnieuw of neem contact op met de klantenservice. ERR-49201');
                    }
                    return;
                }

                updateLastActivity();
                setHasJobStarted(true);

                const total = jobProgressData.total_transactions || 0;
                const processed = jobProgressData.processed_transactions || 0;
                
                const calculatedProgress = total > 0 ? Math.round((processed / total) * 100) : 0;

                setProgress(calculatedProgress);
                setTotalTransactions(total);

                if (jobProgressData.status === 'FAILED') {
                    setJobGroupId(undefined);
                    setError('Er is iets misgegaan tijdens de verbinding met je bank. Ons team is op de hoogte gebracht en zal dit probleem binnenkort onderzoeken. Voor vragen kun je altijd contact met ons opnemen! ERR-38294');
                } else if (jobProgressData.status === 'FINISHED' || (jobProgressData.status === 'FINISHED' && total === 0)) {
                    console.log('Import job finished with status:', jobProgressData.status);
                    setJobGroupId(undefined);
                    setProgress(100);
                    setIsFinished(true);
                }
            } catch (err) {
                console.error('Error polling import job progress:', err);
            }
        };

        const pollInterval = setInterval(poll, POLL_INTERVAL);
        const activityCheck = setInterval(() => {
            if (Date.now() - lastActivityRef.current > INACTIVITY_TIMEOUT) {
                setError('Er is iets misgegaan tijdens de verbinding met je bank. Ons team is op de hoogte gebracht en zal dit probleem binnenkort onderzoeken. Voor vragen kun je altijd contact met ons opnemen! Je kunt het in de tussentijd nog een keer proberen. ERR-94203');
            }
        }, 5000);

        return () => {
            clearInterval(pollInterval);
            clearInterval(activityCheck);
        };
    }, [jobGroupId, isFinished, error, backendApi, updateLastActivity]);

    return { hasJobStarted, progress, totalTransactions, error, isFinished };
}
